import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search, X } from 'lucide-react';

const BOM = () => {
  const [items, setItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const emptyForm = { product_sku: '', product_name: '', version: '1.0', components: [] };
  const [formData, setFormData] = useState({ ...emptyForm });
  const [compInput, setCompInput] = useState({ sku: '', name: '', quantity: 0, unit: 'pcs' });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [bRes, iRes] = await Promise.all([api.get('/production/bom'), api.get('/sales/inventory')]);
      setItems(bRes.data); setInventory(iRes.data);
    } catch(e){} finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm }); setShowModal(true); };
  const openEdit = (b) => { setEditing(b); setFormData({ product_sku: b.product_sku, product_name: b.product_name || '', version: b.version || '1.0', components: b.components || [] }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/production/bom/${editing.id}`, formData);
      else await api.post('/production/bom', formData);
      setShowModal(false); fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete BOM "${code}"?`)) return;
    try { await api.delete(`/production/bom/${id}`); fetchData(); } catch(e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const addComponent = () => {
    if (!compInput.sku || compInput.quantity <= 0) return;
    const inv = inventory.find(i => i.sku === compInput.sku);
    setFormData({ ...formData, components: [...formData.components, { sku: compInput.sku, name: inv?.name || compInput.name, quantity: compInput.quantity, unit: compInput.unit }] });
    setCompInput({ sku: '', name: '', quantity: 0, unit: 'pcs' });
  };

  const removeComponent = (idx) => {
    setFormData({ ...formData, components: formData.components.filter((_, i) => i !== idx) });
  };

  const onProductSelect = (sku) => {
    const inv = inventory.find(i => i.sku === sku);
    setFormData({ ...formData, product_sku: sku, product_name: inv?.name || '' });
  };

  const filtered = items.filter(b => b.bom_code?.toLowerCase().includes(searchTerm.toLowerCase()) || b.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) || b.product_sku?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div data-testid="bom-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Bill of Materials (BOM)</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="add-bom-btn"><Plus size={16} /> Add BOM</button>
      </div>
      <div className="card mb-4">
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search BOM..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>
      <div className="card">
        {loading ? <div>Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>BOM Code</th><th>Product SKU</th><th>Product Name</th><th>Version</th><th>Components</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No BOMs found</td></tr> : filtered.map(b => (
                  <tr key={b.id} data-testid={`bom-row-${b.bom_code}`}>
                    <td className="font-mono">{b.bom_code}</td>
                    <td className="font-mono">{b.product_sku}</td>
                    <td>{b.product_name}</td>
                    <td>{b.version}</td>
                    <td>{(b.components || []).length} items</td>
                    <td><span className={`badge ${b.status === 'active' ? 'badge-success' : 'badge-danger'}`}>{b.status}</span></td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(b)} className="btn btn-ghost btn-sm" data-testid={`edit-btn-${b.bom_code}`}><Edit size={14} /></button>
                        <button onClick={() => handleDelete(b.id, b.bom_code)} className="btn btn-ghost btn-sm" data-testid={`delete-btn-${b.bom_code}`}><Trash2 size={14} color="#DC2626" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showModal && (
        <div className="modal-overlay" data-testid="bom-modal">
          <div className="modal-content" style={{ maxWidth: '650px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? 'Edit BOM' : 'Create BOM'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Product (Finished Good) *</label>
                    <select className="form-select" value={formData.product_sku} onChange={(e) => onProductSelect(e.target.value)} required data-testid="product-select">
                      <option value="">Select Product</option>
                      {inventory.filter(i => i.product_category === 'fg').map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Version</label><input type="text" className="form-input" value={formData.version} onChange={(e) => setFormData({...formData, version: e.target.value})} data-testid="version-input" /></div>
                </div>
                <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                  <label className="form-label" style={{ fontWeight: 600 }}>Components</label>
                  <div className="flex gap-2" style={{ marginBottom: '8px', alignItems: 'flex-end' }}>
                    <div style={{ flex: 2 }}>
                      <select className="form-select" value={compInput.sku} onChange={(e) => { const inv = inventory.find(i => i.sku === e.target.value); setCompInput({...compInput, sku: e.target.value, name: inv?.name || ''}); }} data-testid="comp-sku-select">
                        <option value="">Select Material</option>
                        {inventory.filter(i => i.product_category === 'rm' || i.product_category === 'wip').map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}><input type="number" className="form-input" placeholder="Qty" value={compInput.quantity || ''} onChange={(e) => setCompInput({...compInput, quantity: parseFloat(e.target.value) || 0})} data-testid="comp-qty-input" /></div>
                    <div style={{ flex: 1 }}><input type="text" className="form-input" placeholder="Unit" value={compInput.unit} onChange={(e) => setCompInput({...compInput, unit: e.target.value})} /></div>
                    <button type="button" onClick={addComponent} className="btn btn-secondary btn-sm" data-testid="add-comp-btn"><Plus size={14} /></button>
                  </div>
                  {formData.components.length > 0 && (
                    <div className="table-container" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <table>
                        <thead><tr><th>SKU</th><th>Name</th><th>Qty</th><th>Unit</th><th></th></tr></thead>
                        <tbody>
                          {formData.components.map((c, i) => (
                            <tr key={i}><td className="font-mono">{c.sku}</td><td>{c.name}</td><td>{c.quantity}</td><td>{c.unit}</td><td><button type="button" onClick={() => removeComponent(i)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button></td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-bom-btn">{editing ? 'Update' : 'Create BOM'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BOM;
